import Configs from "../config";
import {
  EditCampaignDataIRoute,
  ICampaignOptions,
  IWebHook,
  ToggleStatusCampaignIRoute,
} from "../generated/Api";
import { Campaign, CampaignFilters } from "../types/CampaignTypes";
import { DefaultResponse } from "../types/UtilTypes";
import { localStorageGetItem } from "../utils/LocalStorage";
import { FindAllContactWithFilterIRoute } from "./Contacts";

export interface IReadCampaign extends Campaign {
  _id: string;
}

interface IUpdateCampaign {
  count: number;
  result: IReadCampaign;
}

interface IDeleteCampaign {
  deleted: number;
}

export const createCampaign = async (
  name: string,
  labels: string[],
  campaignOptions: ICampaignOptions
): Promise<IReadCampaign> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      name,
      labels,
      campaignOptions,
      userUid,
    }),
  });

  const campaigns: DefaultResponse<IReadCampaign> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IReadCampaign;
  }

  throw new Error(campaigns.code);
};

export const readCampaign = async (id: string): Promise<IReadCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/${id}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  const campaigns: DefaultResponse<IReadCampaign> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IReadCampaign;
  }

  throw new Error(campaigns.code);
};

export const cloneCampaign = async (id: string): Promise<IReadCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/${id}/clone`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
  });

  const campaigns: DefaultResponse<IReadCampaign> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IReadCampaign;
  }

  throw new Error(campaigns.code);
};

export const editCampaign = async (
  id: string,
  campaign: EditCampaignDataIRoute
): Promise<IReadCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/edit/${id}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(campaign),
  });

  const campaigns: DefaultResponse<IReadCampaign> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IReadCampaign;
  }

  throw new Error(campaigns.code);
};

export const readAllCampaigns = async (): Promise<IReadCampaign[]> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/campaign/userUid/${userUid}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
    }
  );

  const campaigns: DefaultResponse<IReadCampaign[]> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IReadCampaign[];
  }

  throw new Error(campaigns.code);
};

export const readAllCampaignsWithFilter = async (
  filters: CampaignFilters
): Promise<{ count: number; campaigns: IReadCampaign[] }> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/campaign/paginated/${userUid}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(filters),
    }
  );

  const campaigns: DefaultResponse<{
    count: number;
    campaigns: IReadCampaign[];
  }> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data;
  }

  throw new Error(campaigns.code);
};

export const campaignNameExists = async (
  campaignName: string
): Promise<{ exists: boolean }> => {
  const userUid = localStorageGetItem("USER_UID");
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/campaign/checkName/${userUid}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ campaignName }),
    }
  );

  const campaigns: DefaultResponse<{ exists: boolean }> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data;
  }

  throw new Error(campaigns.code);
};

export const updateCampaignName = async (
  id: string,
  newName: string
): Promise<IUpdateCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/${id}`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify({
      name: newName,
    }),
  });

  const campaigns: DefaultResponse<IUpdateCampaign> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IUpdateCampaign;
  }

  throw new Error(campaigns.code);
};

export const updateCampaign = async (
  id: string,
  campaign: Partial<Campaign>
): Promise<IReadCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/${id}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(campaign),
  });

  const campaigns: any = await response.json();

  if (campaigns.status === "ok") {
    return campaigns.data.result as IReadCampaign;
  }

  throw new Error(campaigns.code);
};

export const launchCampaign = async (id: string): Promise<IReadCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/${id}/launch`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      Authorization: "Bearer " + token,
    },
  });

  const responseData: DefaultResponse<IReadCampaign> = await response.json();
  if (responseData.status === "ok") {
    return (responseData as any).data.result;
  }

  throw new Error(responseData.message);
};

export const toggleCampaignStatus = async (
  id: string,
  data: ToggleStatusCampaignIRoute
): Promise<IReadCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/${id}/status`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-type": "application/json",
      Authorization: "Bearer " + token,
    },
    body: JSON.stringify(data),
  });

  const responseData: DefaultResponse<IReadCampaign> = await response.json();
  if (responseData.status === "ok") {
    return (responseData as any).data.result;
  }

  throw new Error(responseData.message);
};

export const removeCampaignContacts = async (id: string): Promise<boolean> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/campaign/remove-all-contacts/${id}`,
    {
      method: "DELETE",
      headers: {
        Authorization: "Bearer " + token,
      },
    }
  );

  const campaigns: DefaultResponse<boolean> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data;
  }

  throw new Error(campaigns.code);
};

export const deleteCampaign = async (id: string): Promise<IDeleteCampaign> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(`${Configs.SERVER_URL}/campaign/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + token,
    },
  });

  const campaigns: DefaultResponse<IDeleteCampaign> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as IDeleteCampaign;
  }

  throw new Error(campaigns.code);
};

export const testWebhook = async (
  webhook: IWebHook,
  email: string,
  userUid: string
): Promise<Response | null> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/scheduledCampaign/webhook`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        webhook,
        email,
        userUid,
      }),
    }
  );

  const campaigns: DefaultResponse<Response | null> = await response.json();
  if (campaigns.status === "ok") {
    return campaigns.data as Response | null;
  }

  throw new Error(campaigns.code);
};

export type IBulkRemoveFromCampaign = {
  campaignId: string;
} & ({ contactIds: string[] } | { query: FindAllContactWithFilterIRoute });

export const bulkRemoveCampaignContacts = async (
  data: IBulkRemoveFromCampaign
): Promise<number> => {
  const token = localStorageGetItem("USER_TOKEN");
  const response = await fetch(
    `${Configs.SERVER_URL}/campaign/bulk-remove-contacts`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ ...data }),
    }
  );

  const countResponse: DefaultResponse<number> = await response.json();
  if (countResponse.status === "ok") {
    return countResponse.data;
  }

  throw new Error(countResponse.code);
};
